import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Door Knocking",
  "Phone Calls",
  "Lawn Signs",
  "Events & Outreach",
  "Social Media",
  "Donate",
];

export const GetInvolved = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (item) =>
    setInterests((prev) => (prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/volunteers`, { ...form, interests });
      setDone(true);
      toast.success("Thanks for joining the team!");
      setForm({ name: "", email: "", phone: "", message: "" });
      setInterests([]);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="join" className="py-20 lg:py-32 bg-slate-50 border-y border-slate-100" data-testid="get-involved-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-5">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-teal-600">Get Involved</p>
          <h2 className="font-display font-extrabold tracking-tight text-slate-900 text-3xl md:text-4xl lg:text-5xl mt-3 leading-tight">
            Join The Campaign
          </h2>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-md">
            Help bring a fresh voice to Pembina Trails schools. Whether you have an hour or
            an afternoon, every bit of support moves us forward.
          </p>
          <div className="mt-8 rounded-2xl bg-slate-900 text-white p-7">
            <p className="font-display text-xl font-bold">Together, we can strengthen our schools.</p>
            <p className="mt-2 text-slate-300">Community-driven. Student-focused. Forward-looking.</p>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          {done ? (
            <div
              className="h-full rounded-2xl bg-white border border-teal-200 p-10 flex flex-col items-center justify-center text-center"
              data-testid="join-success"
            >
              <CheckCircle2 size={56} className="text-teal-600" />
              <h3 className="font-display text-2xl font-bold text-slate-900 mt-5">You're on the team!</h3>
              <p className="mt-3 text-slate-600 max-w-sm">
                Thank you for joining Noman's campaign. We'll be in touch soon about how you can help.
              </p>
              <button
                onClick={() => setDone(false)}
                className="mt-6 text-teal-700 font-semibold hover:underline"
                data-testid="join-again-button"
              >
                Submit another response
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="rounded-2xl bg-white border border-slate-200 p-7 sm:p-9 shadow-sm"
              data-testid="join-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-slate-700 font-medium">Name</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your full name"
                    className="mt-2 rounded-xl h-12 border-slate-200 focus-visible:ring-teal-500"
                    data-testid="join-input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    className="mt-2 rounded-xl h-12 border-slate-200 focus-visible:ring-teal-500"
                    data-testid="join-input-email"
                  />
                </div>
              </div>

              <div className="mt-5">
                <Label htmlFor="phone" className="text-slate-700 font-medium">Phone</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(204) 000-0000"
                  className="mt-2 rounded-xl h-12 border-slate-200 focus-visible:ring-teal-500"
                  data-testid="join-input-phone"
                />
              </div>

              <div className="mt-6">
                <Label className="text-slate-700 font-medium">Volunteer Interests</Label>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  {INTERESTS.map((item) => {
                    const active = interests.includes(item);
                    return (
                      <button
                        type="button"
                        key={item}
                        onClick={() => toggle(item)}
                        className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                          active
                            ? "bg-teal-600 border-teal-600 text-white"
                            : "bg-white border-slate-200 text-slate-600 hover:border-teal-400"
                        }`}
                        data-testid={`join-interest-${item.toLowerCase().replace(/[^a-z]/g, "")}`}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-6">
                <Label htmlFor="message" className="text-slate-700 font-medium">Message (optional)</Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Anything you'd like Noman to know?"
                  className="mt-2 rounded-xl border-slate-200 focus-visible:ring-teal-500 min-h-[110px]"
                  data-testid="join-input-message"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white font-semibold px-7 py-4 hover:bg-teal-700 transition-colors disabled:opacity-70"
                data-testid="join-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    Join The Team
                    <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
